import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/client";
import ProjectNav from "../components/ProjectNav";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SlotState {
  fileName: string;
  meta: any;
}

interface UploadSlots {
  ev?: SlotState;
  staffingCentre?: SlotState;
  staffingWeekly?: SlotState;
  jiraWp?: SlotState;
  jiraRo?: SlotState;
  ims?: SlotState;
}

// ─── FileSummary (top-level so it never remounts on Upload re-render) ─────────

function FileSummary({ meta, file }: { meta: any; file: File | null }) {
  if (!file && !meta) return null;

  if (meta?._error) {
    return (
      <div className="mt-2 flex items-start gap-1.5 border border-red-200 rounded-lg px-3 py-2 bg-red-50">
        <svg className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        <span className="text-xs text-red-700">{meta._error}</span>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="mt-2 flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
        <svg className="animate-spin w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-xs text-gray-400">Checking file...</span>
      </div>
    );
  }

  const sizeMb = meta?.file_size_mb ?? (file ? (file.size / 1024 / 1024).toFixed(1) : "");
  const rows   = meta?.rows ?? meta?.validation?.row_count ?? null;
  const period = meta?.detected_quarter || meta?.validation?.detected_period || null;

  return (
    <div className="mt-2 flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 bg-white">
      <span className="text-xs text-gray-500">
        {sizeMb}{sizeMb ? " MB" : ""}
        {rows != null ? ` · ${rows} rows` : ""}
        {period ? ` · ${period}` : ""}
      </span>
      <span className="text-xs text-green-600 font-medium">✓ Readable</span>
    </div>
  );
}

// ─── DropZone (top-level — stable identity across Upload re-renders) ──────────

interface DropZoneProps {
  title: string;
  subtitle: string;
  type: string;
  file: File | null;
  restoredName?: string;  // filename from sessionStorage when File object isn't available
  meta: any;
  isDragging: boolean;
  accept?: string;
  onFile: (f: File) => void;
  onClear: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}

function DropZone({
  title, subtitle, type, file, restoredName, meta,
  isDragging, accept = ".xlsx,.xlsm,.csv", onFile, onClear, onDragOver, onDragLeave, onDrop,
}: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const displayName = file?.name || restoredName;
  const hasFile = Boolean(displayName);
  const isError = Boolean(meta?._error);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const getIcon = () => {
    const cls = `w-4 h-4 ${hasFile ? "text-green-600" : "text-gray-400"}`;
    if (type === "staffingCentre" || type === "staffingWeekly") {
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
        </svg>
      );
    }
    if (type === "jiraWp" || type === "jiraRo") {
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      );
    }
    if (type === "ims") {
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); onDragOver(); }}
      onDragLeave={onDragLeave}
      onDrop={e => { e.preventDefault(); onDrop(e); }}
      onClick={!hasFile ? openPicker : undefined}
      className={`rounded-xl border-2 p-5 transition-all
        ${!hasFile ? "cursor-pointer" : ""}
        ${hasFile
          ? isError
            ? "border-red-400 bg-red-50"
            : "border-green-400 bg-green-50"
          : isDragging
          ? "border-blue-400 bg-blue-50"
          : "border-dashed border-gray-300 bg-white hover:border-blue-300"}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${hasFile ? "bg-green-100" : "bg-gray-100"}`}>
            {getIcon()}
          </div>
          <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide text-xs">{title}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasFile && !isError && (
            <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded font-medium border border-green-200">✓ UPLOADED</span>
          )}
          {hasFile && isError && (
            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded font-medium border border-red-200">✗ WRONG FILE</span>
          )}
          {hasFile && (
            <button
              onClick={e => { e.stopPropagation(); onClear(); }}
              className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {hasFile ? (
        <div>
          <p className="text-sm font-bold text-gray-900 truncate">{displayName}</p>
          {restoredName && !file && (
            <p className="text-xs text-gray-400 mt-0.5">Already uploaded — file saved on server</p>
          )}
          <FileSummary meta={meta} file={file} />
        </div>
      ) : (
        <div>
          <p className="text-xs text-gray-400 mb-3">{subtitle}</p>
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-500 bg-white justify-center pointer-events-none">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {isDragging ? "Drop file here!" : "Click to browse or drop file"}
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          // Reset so the same file can be re-selected
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ─── Upload page ──────────────────────────────────────────────────────────────

export default function Upload() {
  const { projectId } = useParams();
  const navigate       = useNavigate();

  const STORAGE_KEY = `upload_${localStorage.getItem("user_id") || "anon"}_${projectId}`;

  // Live File objects (in-session only, can't be persisted)
  const [evFile,              setEvFile]              = useState<File | null>(null);
  const [staffingCentreFile,  setStaffingCentreFile]  = useState<File | null>(null);
  const [staffingWeeklyFile,  setStaffingWeeklyFile]  = useState<File | null>(null);
  const [jiraWpFile,          setJiraWpFile]          = useState<File | null>(null);
  const [jiraRoFile,          setJiraRoFile]          = useState<File | null>(null);
  const [imsFile,             setImsFile]             = useState<File | null>(null);
  const [imsMeta,             setImsMeta]             = useState<any>(null);

  // Server responses (persisted in sessionStorage for back-navigation)
  const [evMeta,              setEvMeta]              = useState<any>(null);
  const [staffingCentreMeta,  setStaffingCentreMeta]  = useState<any>(null);
  const [staffingWeeklyMeta,  setStaffingWeeklyMeta]  = useState<any>(null);
  const [jiraWpMeta,          setJiraWpMeta]          = useState<any>(null);
  const [jiraRoMeta,          setJiraRoMeta]          = useState<any>(null);

  // Restored display state from sessionStorage (fileName only, no File object)
  const [restored, setRestored] = useState<UploadSlots>({});

  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [dragOver, setDragOver] = useState("");

  // ── Restore from sessionStorage on mount ──────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    try {
      const slots: UploadSlots = JSON.parse(saved);
      setRestored(slots);
      if (slots.ev?.meta)             setEvMeta(slots.ev.meta);
      if (slots.staffingCentre?.meta) setStaffingCentreMeta(slots.staffingCentre.meta);
      if (slots.staffingWeekly?.meta) setStaffingWeeklyMeta(slots.staffingWeekly.meta);
      if (slots.jiraWp?.meta)         setJiraWpMeta(slots.jiraWp.meta);
      if (slots.jiraRo?.meta)         setJiraRoMeta(slots.jiraRo.meta);
    } catch {}
  }, [projectId]);

  // ── Save to sessionStorage whenever metas change ──────────────────────────
  // IMPORTANT: read existing storage first and only patch slots we have fresh
  // data for. This prevents the initial render (all metas null) from wiping
  // data that was just restored from storage in the effect above.
  useEffect(() => {
    if (!projectId) return;
    let existing: UploadSlots;
    try { existing = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); }
    catch { existing = {}; }
    let changed = false;
    if (evMeta && !evMeta._error)             { existing.ev             = { fileName: evFile?.name || existing.ev?.fileName || "",             meta: evMeta };             changed = true; }
    if (staffingCentreMeta && !staffingCentreMeta._error) { existing.staffingCentre = { fileName: staffingCentreFile?.name || existing.staffingCentre?.fileName || "", meta: staffingCentreMeta }; changed = true; }
    if (staffingWeeklyMeta && !staffingWeeklyMeta._error) { existing.staffingWeekly = { fileName: staffingWeeklyFile?.name || existing.staffingWeekly?.fileName || "", meta: staffingWeeklyMeta }; changed = true; }
    if (jiraWpMeta && !jiraWpMeta._error)     { existing.jiraWp         = { fileName: jiraWpFile?.name  || existing.jiraWp?.fileName  || "",         meta: jiraWpMeta };         changed = true; }
    if (jiraRoMeta && !jiraRoMeta._error)     { existing.jiraRo         = { fileName: jiraRoFile?.name  || existing.jiraRo?.fileName  || "",         meta: jiraRoMeta };         changed = true; }
    if (changed) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  }, [evMeta, staffingCentreMeta, staffingWeeklyMeta, jiraWpMeta, jiraRoMeta]);

  // ── Upload helper ─────────────────────────────────────────────────────────
  const uploadFile = async (endpoint: string, file: File, setMeta: (m: any) => void) => {
    setMeta(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(endpoint, form);
      setMeta(res.data);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = status === 400
        ? (detail || "Wrong file type")
        : status === 422
        ? (typeof detail === "string" ? detail : "File validation failed — wrong file for this slot")
        : status === 500
        ? (typeof detail === "string" ? detail : "Server error processing file")
        : "Upload failed — please try again";
      setMeta({ _error: msg });
    }
  };

  // ── File handlers ─────────────────────────────────────────────────────────
  const handleFile = useCallback((type: string, file: File) => {
    if (type === "ev")             { setEvFile(file);             uploadFile("/api/upload/ev",                file, setEvMeta); }
    else if (type === "staffingCentre")  { setStaffingCentreFile(file);  uploadFile("/api/upload/staffing",          file, setStaffingCentreMeta); }
    else if (type === "staffingWeekly")  { setStaffingWeeklyFile(file);  uploadFile("/api/upload/staffing-weekly",   file, setStaffingWeeklyMeta); }
    else if (type === "jiraWp")    { setJiraWpFile(file);          uploadFile("/api/upload/jira/workpackages", file, setJiraWpMeta); }
    else if (type === "jiraRo")    { setJiraRoFile(file);          uploadFile("/api/upload/jira/risks",       file, setJiraRoMeta); }
    else if (type === "ims")       { setImsFile(file);  uploadFile("/api/upload/ims", file, setImsMeta); }
  }, []);

  const clearSlot = useCallback((type: string) => {
    if (!projectId) return;
    const slots: UploadSlots = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    delete (slots as any)[type];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
    setRestored(slots);

    if (type === "ev")             { setEvFile(null);            setEvMeta(null); }
    else if (type === "staffingCentre")  { setStaffingCentreFile(null); setStaffingCentreMeta(null); }
    else if (type === "staffingWeekly")  { setStaffingWeeklyFile(null); setStaffingWeeklyMeta(null); }
    else if (type === "jiraWp")    { setJiraWpFile(null);         setJiraWpMeta(null); }
    else if (type === "jiraRo")    { setJiraRoFile(null);         setJiraRoMeta(null); }
    else if (type === "ims")       { setImsFile(null); setImsMeta(null); }
  }, [projectId]);

  const handleDrop = useCallback((e: React.DragEvent, type: string) => {
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(type, file);
  }, [handleFile]);

  // ── Effective metas (live upload or restored) ─────────────────────────────
  const effEvMeta             = evMeta             || restored.ev?.meta             || null;
  const effStaffingCentreMeta = staffingCentreMeta || restored.staffingCentre?.meta || null;
  const effStaffingWeeklyMeta = staffingWeeklyMeta || restored.staffingWeekly?.meta || null;
  const effJiraWpMeta         = jiraWpMeta         || restored.jiraWp?.meta         || null;
  const effJiraRoMeta         = jiraRoMeta         || restored.jiraRo?.meta         || null;

  const hasEv = Boolean(evFile || restored.ev?.fileName);

  const handleNext = () => {
    if (!hasEv) { setError("EVM file is required."); return; }
    setLoading(true); setError("");

    // Persist a bell notification so the user knows files are queued
    const uploadedFiles = [
      effEvMeta             && "EVM",
      effStaffingCentreMeta && "Staffing Centre",
      effStaffingWeeklyMeta && "Weekly Staffing",
      effJiraWpMeta         && "Jira WPs",
      effJiraRoMeta         && "Jira Risks",
    ].filter(Boolean).join(", ");
    api.post("/api/notifications", {
      message: "Files uploaded — ready to process",
      detail:  uploadedFiles ? `Uploaded: ${uploadedFiles}` : "EVM file ready",
      type:    "info",
    }).catch(() => {});

    navigate(`/sanity/${projectId}`, {
      state: {
        evMeta:             effEvMeta,
        staffingCentreMeta: effStaffingCentreMeta,
        staffingWeeklyMeta: effStaffingWeeklyMeta,
        jiraWpMeta:         effJiraWpMeta,
        jiraRoMeta:         effJiraRoMeta,
        imsFilename:        imsFile?.name || restored.ims?.fileName || null,
        imsTmpPath:         imsMeta?.tmp_path || null,
        projectId,
      },
    });
    setLoading(false);
  };

  const steps = ["Upload files", "Run Sanity Checks", "Configure", "Review & generate"];

  const dzProps = (type: string, file: File | null, meta: any, restoredKey: keyof UploadSlots) => ({
    type,
    file,
    restoredName: (!file && restored[restoredKey]?.fileName) ? restored[restoredKey]?.fileName : undefined,
    meta,
    isDragging: dragOver === type,
    onFile: (f: File) => handleFile(type, f),
    onClear: () => clearSlot(type),
    onDragOver: () => setDragOver(type),
    onDragLeave: () => setDragOver(""),
    onDrop: (e: React.DragEvent) => handleDrop(e, type),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={projectId!} active="report" />

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">UPLOAD INPUT FILES</h1>
            <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">
              Drop in EVM and staffing files. Prior quarter is optional but unlocks comparison.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-0 mb-8 mt-6">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                  ${i === 0 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>{i + 1}</div>
                <span className={`text-sm ${i === 0 ? "text-blue-600 font-medium" : "text-gray-400"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <div className="w-8 h-px bg-gray-300 mx-3" />}
            </div>
          ))}
        </div>

        {/* 2×3 grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <DropZone title="M170 EVM Data File" subtitle="Format: M170_BP884_*.xlsx"
            {...dzProps("ev", evFile, effEvMeta, "ev")} />

          <DropZone title="Staffing Centre" subtitle="Format: Staffing*.xlsm · Sheet: SvsD_Pivot"
            {...dzProps("staffingCentre", staffingCentreFile, effStaffingCentreMeta, "staffingCentre")} />

          <DropZone title="Staffing Weekly" subtitle="Format: SC_Wkly_Actuals*.xlsm · Sheet: BP884"
            {...dzProps("staffingWeekly", staffingWeeklyFile, effStaffingWeeklyMeta, "staffingWeekly")} />

          <DropZone title="Jira Work Packages" subtitle="Format: Jira*.csv · Work package issues"
            {...dzProps("jiraWp", jiraWpFile, effJiraWpMeta, "jiraWp")} />

          <DropZone title="Jira Risks & Opportunities" subtitle="Format: Jira*RO*.csv · Risk register"
            {...dzProps("jiraRo", jiraRoFile, effJiraRoMeta, "jiraRo")} />

          <DropZone title="IMS File" subtitle="Format: *.mpp / *.csv · MS Project milestone schedule"
            accept=".mpp,.csv" {...dzProps("ims", imsFile, imsMeta, "ims")} />
        </div>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        <div className="flex justify-between">
          <button onClick={() => navigate("/select-project")}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            ← Back
          </button>
          <button onClick={handleNext} disabled={loading || !hasEv}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {loading ? "Processing..." : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}
